import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { format, parseISO } from 'date-fns';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import CompaniesPage from '@/pages/CompaniesPage';
import { AsyncSearchSelect } from '@/components/AsyncSearchSelect';
import CompanyLogo from '@/components/ui/CompanyLogo';
import SuggestedContactsBlock, { ColoredAvatar } from '@/components/SuggestedContactsBlock';

function fmtDate(d) {
  if (!d) return '';
  try { return format(parseISO(d), 'MMM d, yyyy'); } catch (e) { return ''; }
}
function fmtDateTime(d) {
  if (!d) return '';
  try { return format(parseISO(d), 'MMM d, HH:mm'); } catch (e) { return ''; }
}
function fmtCurrency(v) {
  if (!v) return '0';
  return Number(v).toLocaleString('en-IN');
}

var TIMELINE_ICONS = {
  activity: { color: 'bg-blue-500', path: 'M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z' },
  task: { color: 'bg-amber-500', path: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4' },
  opportunity: { color: 'bg-green-500', path: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
  presales: { color: 'bg-purple-500', path: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z' },
  email: { color: 'bg-sky-500', path: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' },
  whatsapp: { color: 'bg-emerald-500', path: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z' },
  sms: { color: 'bg-indigo-500', path: 'M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z' },
  call: { color: 'bg-rose-500', path: 'M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z' },
};

var HEALTH_STYLES = {
  hot: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-300', dot: 'bg-green-500', label: 'Hot' },
  warm: { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-300', dot: 'bg-amber-500', label: 'Warm' },
  cold: { bg: 'bg-slate-100 dark:bg-slate-700', text: 'text-slate-500 dark:text-slate-400', dot: 'bg-slate-400', label: 'Cold' },
  dead: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-300', dot: 'bg-red-500', label: 'Dead' },
};

function HealthBadge({ health }) {
  if (!health) return null;
  const style = HEALTH_STYLES[health.tag] || HEALTH_STYLES.dead;
  return (
    <span
      className={'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ' + style.bg + ' ' + style.text}
      title={'Health score: ' + health.score + '/100'}
      data-testid={'health-badge-' + health.tag}
    >
      <span className={'w-1.5 h-1.5 rounded-full ' + style.dot}></span>
      {style.label} · {health.score}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────── Accounts list

function AccountListPanel(props) {
  var { accounts, selectedId, onSelect, search, setSearch, loading, pagination, onPageChange, sortBy, setSortBy } = props;
  return (
    <div className="flex flex-col h-full" data-testid="account-list-panel">
      <div className="p-3 border-b border-slate-200 dark:border-slate-700">
        <h2 className="text-sm font-bold text-slate-900 dark:text-white mb-2">Accounts</h2>
        <input
          type="text" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search companies..."
          className="w-full px-3 py-2 text-xs border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500 mb-2"
          data-testid="account-search"
        />
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="w-full px-2 py-1.5 text-xs border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
          data-testid="account-sort"
        >
          <option value="last_activity">Last Activity</option>
          <option value="health">Health Score</option>
          <option value="pipeline_value">Pipeline Value</option>
          <option value="lead_count">Lead Count</option>
          <option value="name">Name A→Z</option>
        </select>
      </div>
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-8"><div className="w-5 h-5 border-2 border-slate-200 border-t-slate-600 rounded-full animate-spin"></div></div>
        ) : accounts.length === 0 ? (
          <p className="text-xs text-slate-400 text-center py-8">No accounts found</p>
        ) : accounts.map(function(acc) {
          var isActive = selectedId === acc.id;
          return (
            <div
              key={acc.id}
              onClick={() => onSelect(acc.id)}
              className={'px-3 py-3 border-b border-slate-100 dark:border-slate-700 cursor-pointer transition-colors ' + (isActive ? 'bg-blue-50 dark:bg-blue-900/20 border-l-2 border-l-blue-500' : 'hover:bg-slate-50 dark:hover:bg-slate-700/50')}
              data-testid={'account-item-' + acc.id}
            >
              <div className="flex items-start gap-2.5">
                <CompanyLogo domain={(acc.website || '').replace(/^https?:\/\//, '').replace(/\/.*$/, '') || null} name={acc.name} size={32} className="shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-semibold text-slate-900 dark:text-white truncate flex-1">{acc.name}</p>
                    <HealthBadge health={acc.health} />
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    {acc.industry && <span className="text-[10px] px-1.5 py-0.5 bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 rounded">{acc.industry}</span>}
                    {acc.city && <span className="text-[10px] text-slate-400">{acc.city}</span>}
                  </div>
                  <div className="flex items-center gap-3 mt-1.5 text-[10px] text-slate-500 dark:text-slate-400">
                    <span>{acc.lead_count} leads</span>
                    <span>{acc.contact_count} contacts</span>
                    {acc.pipeline_value > 0 && <span className="text-green-600 dark:text-green-400 font-medium">₹{fmtCurrency(acc.pipeline_value)}</span>}
                  </div>
                  {acc.last_activity && <p className="text-[10px] text-slate-400 mt-1">Last: {fmtDate(acc.last_activity)}</p>}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {pagination && pagination.pages > 1 && (
        <div className="p-2 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between">
          <button disabled={pagination.page <= 1} onClick={() => onPageChange(pagination.page - 1)} className="px-2 py-1 text-[10px] border rounded text-slate-500 dark:text-slate-400 border-slate-300 dark:border-slate-600 disabled:opacity-30">Prev</button>
          <span className="text-[10px] text-slate-500">{pagination.page}/{pagination.pages}</span>
          <button disabled={pagination.page >= pagination.pages} onClick={() => onPageChange(pagination.page + 1)} className="px-2 py-1 text-[10px] border rounded text-slate-500 dark:text-slate-400 border-slate-300 dark:border-slate-600 disabled:opacity-30">Next</button>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────── Timeline with Quick Actions + filter

var TIMELINE_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'activity', label: 'Activities' },
  { id: 'call', label: 'Calls' },
  { id: 'email', label: 'Email' },
  { id: 'whatsapp', label: 'WhatsApp' },
  { id: 'task', label: 'Tasks' },
  { id: 'opportunity', label: 'Opps' },
  { id: 'presales', label: 'Pre-Sales' },
];

function QuickActionsBar({ leads, onAction }) {
  const defaultLeadId = (leads && leads[0] && leads[0].id) || null;
  return (
    <div className="flex flex-wrap gap-1.5 p-2 border-b border-slate-100 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-900/40" data-testid="quick-actions-bar">
      <button onClick={() => onAction('log_call', defaultLeadId)} disabled={!defaultLeadId} className="px-2.5 py-1 text-[11px] font-medium bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-md hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed text-slate-700 dark:text-slate-200" data-testid="qa-log-call">☎ Log Call</button>
      <button onClick={() => onAction('send_email', defaultLeadId)} className="px-2.5 py-1 text-[11px] font-medium bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-md hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200" data-testid="qa-send-email">✉ Send Email</button>
      <button onClick={() => onAction('add_task', defaultLeadId)} disabled={!defaultLeadId} className="px-2.5 py-1 text-[11px] font-medium bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-md hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed text-slate-700 dark:text-slate-200" data-testid="qa-add-task">+ Task</button>
      <button onClick={() => onAction('add_note', defaultLeadId)} disabled={!defaultLeadId} className="px-2.5 py-1 text-[11px] font-medium bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-md hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed text-slate-700 dark:text-slate-200" data-testid="qa-add-note">✎ Note</button>
      <button onClick={() => onAction('request_presales', defaultLeadId)} disabled={!defaultLeadId} className="px-2.5 py-1 text-[11px] font-medium bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-md hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed text-slate-700 dark:text-slate-200" data-testid="qa-request-presales">⚙ Pre-Sales</button>
    </div>
  );
}

function TimelinePanel(props) {
  var { timeline, loading, companyName, pagination, onLoadMore, filter, setFilter, leads, onQuickAction } = props;
  const [expandedIdx, setExpandedIdx] = useState(null);

  if (!companyName) {
    return (
      <div className="flex items-center justify-center h-full text-slate-400 dark:text-slate-500" data-testid="timeline-empty">
        <div className="text-center">
          <svg className="w-10 h-10 mx-auto mb-2 opacity-40" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
          <p className="text-sm">Select an account to view activity</p>
        </div>
      </div>
    );
  }

  const filtered = filter === 'all' ? timeline : timeline.filter(t => t.type === filter);

  // ─── Group emails into threads (by normalised subject) ────────────────
  // Strip `Re:`/`Fwd:` prefixes so "Re: Proposal" and "Proposal" fold into one thread.
  function normalizeSubject(s) {
    return (s || '').replace(/^(\s*(re|fw|fwd)\s*:\s*)+/i, '').replace(/^📥\s*|^📤\s*/, '').trim().toLowerCase();
  }
  const emailItems = filtered.filter(t => t.type === 'email');
  const nonEmailItems = filtered.filter(t => t.type !== 'email');
  const threads = {};
  emailItems.forEach(e => {
    const key = normalizeSubject(e.title) || '(no subject)';
    if (!threads[key]) {
      threads[key] = {
        key,
        subject: (e.title || '').replace(/^📥\s*|^📤\s*/, '').replace(/^(\s*(re|fw|fwd)\s*:\s*)+/i, '').trim() || '(no subject)',
        messages: [],
        last_at: '',
        unread: 0,
        participants: new Set(),
        latest_email_id: null,
        has_inbound: false,
      };
    }
    const t = threads[key];
    t.messages.push(e);
    if (!t.last_at || (e.created_at || '') > t.last_at) {
      t.last_at = e.created_at || '';
      t.latest_email_id = e.email_id;
    }
    if (e.is_read === false) t.unread += 1;
    if (e.from) t.participants.add(e.from);
    if (e.subtype === 'inbound') t.has_inbound = true;
  });
  const threadList = Object.values(threads).sort((a, b) => (b.last_at || '').localeCompare(a.last_at || ''));

  return (
    <div className="flex flex-col h-full" data-testid="timeline-panel">
      <div className="p-3 border-b border-slate-200 dark:border-slate-700">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-bold text-slate-900 dark:text-white">{companyName} — Timeline</h2>
          <p className="text-[10px] text-slate-500 dark:text-slate-400">{filtered.length} of {pagination ? pagination.total : 0} events</p>
        </div>
      </div>
      <QuickActionsBar leads={leads} onAction={onQuickAction} />
      <div className="flex gap-1 p-2 border-b border-slate-100 dark:border-slate-700 overflow-x-auto scrollbar-hide" data-testid="timeline-filters">
        {TIMELINE_FILTERS.map(f => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={'px-2.5 py-1 text-[11px] rounded-full whitespace-nowrap transition-colors ' + (filter === f.id ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900' : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200')}
            data-testid={'tl-filter-' + f.id}
          >{f.label}</button>
        ))}
      </div>
      <div className="flex-1 overflow-y-auto p-3">
        {loading ? (
          <div className="flex items-center justify-center py-8"><div className="w-5 h-5 border-2 border-slate-200 border-t-slate-600 rounded-full animate-spin"></div></div>
        ) : filtered.length === 0 ? (
          <p className="text-xs text-slate-400 text-center py-8">No events match this filter</p>
        ) : (
          <div className="space-y-0.5">
            {/* ── Email threads (pinned to top, clickable to open inbox) ── */}
            {threadList.length > 0 && (
              <div className="mb-4 rounded-lg border border-blue-200 dark:border-blue-900/50 bg-blue-50/40 dark:bg-blue-900/10 overflow-hidden">
                <div className="flex items-center justify-between px-3 py-2 border-b border-blue-200/60 dark:border-blue-900/40">
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-blue-600 dark:text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-blue-900 dark:text-blue-200">
                      Email threads ({threadList.length})
                    </h3>
                  </div>
                  <span className="text-[10px] text-blue-700/70 dark:text-blue-300/70">click any thread to open</span>
                </div>
                <div className="divide-y divide-blue-100 dark:divide-blue-900/30" data-testid="email-threads-list">
                  {threadList.slice(0, 8).map(function(t) {
                    const partList = Array.from(t.participants).slice(0, 3);
                    const partText = partList.join(', ') + (t.participants.size > 3 ? ` +${t.participants.size - 3}` : '');
                    return (
                      <Link
                        key={t.key}
                        to={t.latest_email_id ? `/email?open=${t.latest_email_id}` : '/email'}
                        className="group flex items-start gap-3 px-3 py-2.5 bg-white dark:bg-slate-800 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                        data-testid={`email-thread-${t.key.slice(0, 30)}`}
                      >
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${t.has_inbound ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300' : 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'}`}>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`text-xs truncate ${t.unread > 0 ? 'font-bold text-slate-900 dark:text-white' : 'font-medium text-slate-800 dark:text-slate-100'} group-hover:text-blue-600 dark:group-hover:text-blue-400`}>
                              {t.subject}
                            </span>
                            {t.messages.length > 1 && (
                              <span className="text-[10px] px-1.5 py-0.5 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 rounded tabular-nums flex-shrink-0">
                                {t.messages.length}
                              </span>
                            )}
                            {t.unread > 0 && (
                              <span className="text-[10px] px-1.5 py-0.5 bg-blue-600 text-white rounded-full font-semibold tabular-nums flex-shrink-0">
                                {t.unread} new
                              </span>
                            )}
                          </div>
                          {partText && <p className="text-[11px] text-slate-500 dark:text-slate-400 truncate mt-0.5">{partText}</p>}
                        </div>
                        <div className="flex flex-col items-end gap-1 flex-shrink-0">
                          <span className="text-[10px] text-slate-400 whitespace-nowrap">{fmtDateTime(t.last_at)}</span>
                          <svg className="w-3 h-3 text-slate-300 dark:text-slate-600 group-hover:text-blue-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                        </div>
                      </Link>
                    );
                  })}
                  {threadList.length > 8 && (
                    <Link
                      to={`/email?q=${encodeURIComponent(companyName)}`}
                      className="block text-center text-xs text-blue-600 dark:text-blue-400 hover:underline py-1.5 bg-white dark:bg-slate-800"
                      data-testid="email-threads-view-all"
                    >
                      View all {threadList.length} threads in Inbox →
                    </Link>
                  )}
                </div>
              </div>
            )}

            {nonEmailItems.length > 0 && threadList.length > 0 && (
              <div className="flex items-center gap-2 mt-4 mb-1">
                <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700"></div>
                <h3 className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Other activity</h3>
                <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700"></div>
              </div>
            )}

            {/* ── Non-email timeline items (tasks/calls/WhatsApp/notes/...) ── */}
            {nonEmailItems.map(function(item, idx) {
              var icon = TIMELINE_ICONS[item.type] || TIMELINE_ICONS.activity;
              const isExp = expandedIdx === idx;
              return (
                <div key={(item.created_at || '') + '-' + idx} className="flex gap-3 py-2">
                  <div className="flex flex-col items-center flex-shrink-0">
                    <div className={'w-7 h-7 rounded-full flex items-center justify-center ' + icon.color}>
                      <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={icon.path} /></svg>
                    </div>
                    {idx < nonEmailItems.length - 1 && <div className="w-px flex-1 bg-slate-200 dark:bg-slate-700 mt-1"></div>}
                  </div>
                  <div className="flex-1 min-w-0 pb-2">
                    <div className="flex items-center gap-2 cursor-pointer" onClick={() => setExpandedIdx(isExp ? null : idx)}>
                      <span className="text-xs font-medium text-slate-800 dark:text-white truncate">{item.title}</span>
                      <span className="text-[10px] text-slate-400 whitespace-nowrap">{fmtDateTime(item.created_at)}</span>
                    </div>
                    {item.description && <p className={'text-xs text-slate-500 dark:text-slate-400 mt-0.5 ' + (isExp ? '' : 'line-clamp-2')}>{item.description}</p>}
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      {item.lead_name && <Link to={'/leads/' + item.lead_id} className="text-[10px] text-blue-500 hover:underline">{item.lead_name}</Link>}
                      {item.status && <span className={'text-[10px] px-1.5 py-0.5 rounded ' + (item.status === 'done' || item.status === 'completed' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400')}>{item.status}</span>}
                      {item.from && <span className="text-[10px] text-slate-500">from {item.from}</span>}
                    </div>
                  </div>
                </div>
              );
            })}
            {pagination && pagination.page < pagination.pages && (
              <button onClick={onLoadMore} className="w-full py-2 text-xs text-blue-600 dark:text-blue-400 hover:underline" data-testid="load-more-timeline">Load more</button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────── Intelligence panel

function IntelligencePanel(props) {
  var { intel, loading, onContactsChanged } = props;
  if (!intel) {
    return <div className="flex items-center justify-center h-full text-slate-400 dark:text-slate-500" data-testid="intel-empty"><p className="text-sm">Select an account</p></div>;
  }
  if (loading) {
    return <div className="flex items-center justify-center h-full"><div className="w-5 h-5 border-2 border-slate-200 border-t-slate-600 rounded-full animate-spin"></div></div>;
  }
  var comp = intel.company || {};
  var m = intel.metrics || {};
  return (
    <div className="flex flex-col h-full overflow-y-auto" data-testid="intelligence-panel">
      <div className="p-3 border-b border-slate-200 dark:border-slate-700">
        <h2 className="text-sm font-bold text-slate-900 dark:text-white">Account Intelligence</h2>
      </div>
      <div className="p-3 space-y-4">
        <div>
          <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">Company</h3>
          <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-3 space-y-1.5">
            <p className="text-sm font-semibold text-slate-900 dark:text-white">{comp.name}</p>
            {comp.industry && <p className="text-[11px] text-slate-500">{comp.industry}</p>}
            {comp.website && <a href={comp.website} target="_blank" rel="noopener noreferrer" className="block text-[11px] text-blue-500 hover:underline truncate">{comp.website}</a>}
            <div className="flex items-center gap-3 pt-1 flex-wrap">
              {comp.employee_size && <span className="text-[10px] text-slate-500">👥 {comp.employee_size}</span>}
              {comp.city && <span className="text-[10px] text-slate-500">📍 {comp.city}{comp.state ? ', ' + comp.state : ''}</span>}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-2 text-center"><p className="text-lg font-bold text-blue-700 dark:text-blue-300">{m.total_leads || 0}</p><p className="text-[10px] text-slate-500">Leads</p></div>
          <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-2 text-center"><p className="text-lg font-bold text-purple-700 dark:text-purple-300">{m.total_contacts || 0}</p><p className="text-[10px] text-slate-500">Contacts</p></div>
          <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-2 text-center"><p className="text-sm font-bold text-green-700 dark:text-green-300">₹{fmtCurrency(m.total_pipeline)}</p><p className="text-[10px] text-slate-500">Pipeline</p></div>
          <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-2 text-center"><p className="text-lg font-bold text-amber-700 dark:text-amber-300">{m.win_rate || 0}%</p><p className="text-[10px] text-slate-500">Win Rate</p></div>
        </div>

        {/* ── Salesflare-style suggested contacts (auto-discovered) ──── */}
        {comp.id && (
          <SuggestedContactsBlock
            companyId={comp.id}
            compact={true}
            onContactsChanged={onContactsChanged}
          />
        )}

        {intel.contacts && intel.contacts.length > 0 && (
          <div>
            <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">Contacts ({m.total_contacts || intel.contacts.length})</h3>
            <div className="space-y-1 max-h-96 overflow-y-auto pr-1" data-testid="account-intel-contacts-list">
              {intel.contacts.map(function(c) { return (
                <div key={c.id} className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800/50 group" data-testid={`account-intel-contact-${c.id}`}>
                  <ColoredAvatar name={c.name} email={c.email} size={28} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-1">
                      <p className="text-xs font-medium text-slate-800 dark:text-white truncate">{c.name}</p>
                      {(c.is_primary || c.is_primary_contact) && <span className="text-[10px] px-1 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300 rounded flex-shrink-0">Primary</span>}
                    </div>
                    {(c.designation || c.role) && <p className="text-[10px] text-slate-500 truncate">{c.designation || c.role}</p>}
                    {c.email && <p className="text-[10px] text-slate-400 truncate">{c.email}</p>}
                  </div>
                  {c.email && (
                    <a
                      href={`/email?compose=1&to=${encodeURIComponent(c.email)}`}
                      className="p-1 rounded text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                      title={`Email ${c.name}`}
                      onClick={(e) => e.stopPropagation()}
                      data-testid={`compose-contact-${c.id}`}
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                    </a>
                  )}
                </div>
              ); })}
            </div>
          </div>
        )}
        {intel.opportunities && intel.opportunities.length > 0 && (
          <div>
            <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">Opportunities ({intel.opportunities.length})</h3>
            <div className="space-y-1.5">
              {intel.opportunities.slice(0, 5).map(function(o) { return (
                <Link key={o.id} to={'/opportunities/' + o.id} className="block p-2 bg-slate-50 dark:bg-slate-900 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800">
                  <div className="flex items-center justify-between"><span className="text-xs font-medium text-slate-800 dark:text-white truncate">{o.name}</span><span className="text-xs font-bold text-green-600">₹{fmtCurrency(o.deal_value)}</span></div>
                  <div className="flex items-center gap-2 mt-0.5"><span className="text-[10px] text-slate-500">{o.stage}</span><span className="text-[10px] text-slate-400">{o.probability}%</span></div>
                </Link>
              ); })}
            </div>
          </div>
        )}
        {intel.presales && intel.presales.length > 0 && (
          <div>
            <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">Pre-Sales ({intel.presales.length})</h3>
            <div className="space-y-1.5">
              {intel.presales.map(function(p) { return (
                <div key={p.id} className="p-2 bg-slate-50 dark:bg-slate-900 rounded-lg">
                  <p className="text-xs font-medium text-slate-800 dark:text-white">{p.title}</p>
                  <div className="flex items-center gap-2 mt-0.5"><span className="text-[10px] text-slate-500">{(p.request_type || '').replace(/_/g, ' ')}</span><span className="text-[10px] px-1 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-300 rounded">{p.status}</span>{p.technical_score != null && <span className="text-[10px] font-medium text-slate-600">{p.technical_score}/100</span>}</div>
                </div>
              ); })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────── Suggestions mode panels

function SuggestionListPanel({ suggestions, selectedDomain, onSelect, onAccept, onDismiss, loading, onRefresh }) {
  return (
    <div className="flex flex-col h-full" data-testid="suggestion-list-panel">
      <div className="p-3 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
        <div>
          <h2 className="text-sm font-bold text-slate-900 dark:text-white">Suggested Accounts</h2>
          <p className="text-[10px] text-slate-500 mt-0.5">{suggestions.length} new from your inbox</p>
        </div>
        <button onClick={onRefresh} className="px-2 py-1 text-[11px] border border-slate-300 dark:border-slate-600 rounded-md text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700" data-testid="suggestions-refresh">Refresh</button>
      </div>
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-8"><div className="w-5 h-5 border-2 border-slate-200 border-t-slate-600 rounded-full animate-spin"></div></div>
        ) : suggestions.length === 0 ? (
          <div className="text-center py-12 px-4">
            <svg className="w-12 h-12 mx-auto mb-3 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
            <p className="text-xs text-slate-500">No new suggestions — you're all caught up.</p>
          </div>
        ) : suggestions.map(s => {
          const isActive = selectedDomain === s.domain;
          return (
            <div key={s.domain} onClick={() => onSelect(s.domain)}
              className={'px-3 py-3 border-b border-slate-100 dark:border-slate-700 cursor-pointer transition-colors ' + (isActive ? 'bg-blue-50 dark:bg-blue-900/20 border-l-2 border-l-blue-500' : 'hover:bg-slate-50 dark:hover:bg-slate-700/50')}
              data-testid={'suggestion-item-' + s.domain}>
              <div className="flex items-start gap-3">
                <CompanyLogo domain={s.domain} name={s.name_guess} size={36} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-slate-900 dark:text-white truncate">{s.name_guess}</p>
                      <p className="text-[10px] text-slate-500 truncate">{s.domain}</p>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <button onClick={(e) => { e.stopPropagation(); onAccept(s); }} className="w-6 h-6 flex items-center justify-center rounded bg-green-100 hover:bg-green-200 dark:bg-green-900/30 dark:hover:bg-green-900/50 text-green-700 dark:text-green-300" title="Create account" data-testid={'suggestion-accept-' + s.domain}>✓</button>
                      <button onClick={(e) => { e.stopPropagation(); onDismiss(s); }} className="w-6 h-6 flex items-center justify-center rounded bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-500 dark:text-slate-400" title="Dismiss" data-testid={'suggestion-dismiss-' + s.domain}>✕</button>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 mt-1.5 text-[10px] text-slate-500 dark:text-slate-400">
                    <span>{s.days_ago != null ? (s.days_ago === 0 ? 'today' : s.days_ago === 1 ? '1 day ago' : s.days_ago + ' days ago') : ''}</span>
                    <span>{s.contact_count} contact{s.contact_count === 1 ? '' : 's'}</span>
                    <span>{s.email_count} email{s.email_count === 1 ? '' : 's'}</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SuggestionDetailPanel({ detail, loading, onAccept, onQuickCreate, onDismiss, suggestion, quickCreating }) {
  if (!suggestion) {
    return (
      <div className="flex items-center justify-center h-full text-slate-400 dark:text-slate-500 p-6 text-center" data-testid="suggestion-empty">
        <div>
          <svg className="w-16 h-16 mx-auto mb-4 opacity-40" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m8 0a2 2 0 012 2v12H4V8a2 2 0 012-2m8 0H8" /></svg>
          <h3 className="text-base font-semibold text-slate-700 dark:text-slate-300 mb-1">Create an account if you're selling to this company</h3>
          <p className="text-xs text-slate-500 max-w-xs mx-auto">After that, add contacts to it to automatically keep track of all emails, meetings, calls, and more.</p>
        </div>
      </div>
    );
  }
  if (loading || !detail) {
    return <div className="flex items-center justify-center h-full"><div className="w-5 h-5 border-2 border-slate-200 border-t-slate-600 rounded-full animate-spin"></div></div>;
  }

  const cleanWebsite = (detail.website || '').replace(/^https?:\/\//, '').replace(/\/$/, '');

  return (
    <div className="flex flex-col h-full overflow-y-auto" data-testid="suggestion-detail-panel">
      {/* ── Hero header with logo + name + sticky CTA ───────────────────── */}
      <div className="px-4 pt-5 pb-4 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
        <div className="flex items-start gap-3 mb-4">
          <CompanyLogo domain={detail.domain || suggestion.domain} name={detail.name_guess} size={48} />
          <div className="min-w-0 flex-1">
            <h2 className="text-base font-bold text-slate-900 dark:text-white truncate">{detail.name_guess}</h2>
            <p className="text-xs text-slate-500 truncate">{suggestion.domain}</p>
          </div>
        </div>

        {/* Big primary "Create account" button — quick-create path */}
        <button
          onClick={() => onQuickCreate(suggestion, detail)}
          disabled={quickCreating}
          className="w-full px-3 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-md transition-colors inline-flex items-center justify-center gap-2"
          data-testid="suggestion-quick-create"
        >
          {quickCreating ? (
            <>
              <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Creating…
            </>
          ) : 'Create account'}
        </button>

        {/* Customise / dismiss links */}
        <div className="flex items-center justify-between mt-2.5 text-[11px]">
          <button
            onClick={() => onAccept(suggestion)}
            className="text-blue-600 dark:text-blue-400 hover:underline"
            data-testid="suggestion-detail-customize"
          >
            Customise before creating
          </button>
          <button
            onClick={() => onDismiss(suggestion)}
            className="text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
            data-testid="suggestion-detail-dismiss"
          >
            Dismiss
          </button>
        </div>
      </div>

      {/* ── Body ───────────────────────────────────────────────────────── */}
      <div className="p-4 space-y-5">
        {/* Company Information */}
        <section>
          <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">Company information</h3>
          <a
            href={detail.website}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-2.5 py-2 -mx-1 rounded-md hover:bg-slate-50 dark:hover:bg-slate-700/50 group"
          >
            <span className="w-6 h-6 inline-flex items-center justify-center rounded text-slate-400 group-hover:text-blue-500">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z M3.6 9h16.8M3.6 15h16.8 M11.5 3a17 17 0 000 18 M12.5 3a17 17 0 010 18" /></svg>
            </span>
            <span className="text-xs text-slate-700 dark:text-slate-200 group-hover:text-blue-600 dark:group-hover:text-blue-400 truncate">{cleanWebsite || detail.website}</span>
          </a>
        </section>

        {/* People in my network — colleagues at intalk.io who emailed this domain */}
        {detail.people_in_my_network && detail.people_in_my_network.length > 0 && (
          <section>
            <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">People in my network</h3>
            <p className="text-[10px] text-slate-500 mb-2">Colleagues who've also emailed this domain</p>
            <div className="space-y-1">
              {detail.people_in_my_network.map(p => (
                <NetworkPersonRow key={p.id} person={p} />
              ))}
            </div>
          </section>
        )}

        {/* Contacts at this domain */}
        {detail.contacts_at_domain && detail.contacts_at_domain.length > 0 && (
          <section>
            <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">
              Contacts at this domain ({detail.contact_count})
            </h3>
            <div className="space-y-1">
              {(detail.contacts_at_domain || []).slice(0, 8).map(c => (
                <ContactAtDomainRow key={c.email} contact={c} />
              ))}
            </div>
          </section>
        )}

        {/* Recent emails */}
        {detail.sample_emails && detail.sample_emails.length > 0 && (
          <section>
            <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">Recent emails</h3>
            <div className="space-y-1.5">
              {detail.sample_emails.map((e, i) => (
                <div key={i} className="p-2 bg-slate-50 dark:bg-slate-900/50 rounded-md border border-slate-100 dark:border-slate-700/60">
                  <p className="text-xs font-medium text-slate-800 dark:text-white truncate">{e.subject || '(no subject)'}</p>
                  <p className="text-[10px] text-slate-500 truncate">from {e.from_name || e.from_email} · {fmtDate(e.received_at)}</p>
                  {e.preview && <p className="text-[10px] text-slate-400 mt-1 line-clamp-2">{e.preview}</p>}
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

// ─── Coloured initial avatar (deterministic by name) ────────────────────
const _AVATAR_TONES = [
  'bg-amber-400 text-white',
  'bg-rose-400 text-white',
  'bg-emerald-400 text-white',
  'bg-sky-400 text-white',
  'bg-indigo-400 text-white',
  'bg-purple-400 text-white',
  'bg-orange-400 text-white',
  'bg-teal-400 text-white',
];
function _avatarTone(seed = '') {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h << 5) - h + seed.charCodeAt(i);
  return _AVATAR_TONES[Math.abs(h) % _AVATAR_TONES.length];
}

function NetworkPersonRow({ person }) {
  const initial = (person.name || person.email || '?').slice(0, 1).toUpperCase();
  const composeHref = person.email
    ? `/email?compose=1&to=${encodeURIComponent(person.email)}`
    : null;
  return (
    <div className="flex items-center gap-2.5 px-2 py-1.5 -mx-1 rounded-md hover:bg-slate-50 dark:hover:bg-slate-700/50 group">
      <span className={`w-7 h-7 rounded-full inline-flex items-center justify-center text-xs font-semibold flex-shrink-0 ${_avatarTone(person.id || person.name || person.email)}`}>
        {initial}
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium text-slate-800 dark:text-white truncate">{person.name || person.email}</p>
        {person.role && (
          <p className="text-[10px] text-slate-500 capitalize truncate">{(person.role || '').replace(/_/g, ' ')}</p>
        )}
      </div>
      {composeHref && (
        <a
          href={composeHref}
          className="p-1.5 rounded text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 opacity-0 group-hover:opacity-100 transition-opacity"
          title={`Email ${person.name || person.email}`}
          onClick={(e) => e.stopPropagation()}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
        </a>
      )}
    </div>
  );
}

function ContactAtDomainRow({ contact }) {
  const initial = (contact.name || contact.email || '?').slice(0, 1).toUpperCase();
  return (
    <div className="flex items-center gap-2.5 px-2 py-1.5 -mx-1 rounded-md hover:bg-slate-50 dark:hover:bg-slate-700/50">
      <span className={`w-7 h-7 rounded-full inline-flex items-center justify-center text-xs font-semibold flex-shrink-0 ${_avatarTone(contact.email || contact.name)}`}>
        {initial}
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium text-slate-800 dark:text-white truncate">{contact.name || contact.email}</p>
        <p className="text-[10px] text-slate-500 truncate">{contact.email}</p>
      </div>
      {contact.last_seen && (
        <span className="text-[10px] text-slate-400 flex-shrink-0">{fmtDate(contact.last_seen)}</span>
      )}
    </div>
  );
}

// ──────────────────────────────────── Accept Suggestion wizard modal
function AcceptSuggestionModal({ suggestion, detail, onClose, onComplete }) {
  const defaultCompanyName = (suggestion?.name_guess || '').trim();
  const defaultDomain = suggestion?.domain || '';

  // Step state
  const [step, setStep] = useState(1); // 1 = company, 2 = contacts, 3 = lead
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Company
  const [companyName, setCompanyName] = useState(defaultCompanyName);
  const [industry, setIndustry] = useState('');
  const [employeeSize, setEmployeeSize] = useState('');
  const [city, setCity] = useState('');

  // Contacts — seed from detail.contacts_at_domain
  const seedContacts = (detail?.contacts_at_domain || []).map(c => ({
    email: c.email,
    name: c.name || (c.email || '').split('@')[0],
    designation: '',
    phone: '',
    is_primary_contact: false,
    is_decision_maker: false,
    influence_level: 'medium',
    selected: true,
  }));
  const [contacts, setContacts] = useState(seedContacts);

  // Lead
  const [leadContactEmail, setLeadContactEmail] = useState(seedContacts[0]?.email || '');
  const [leadStatus, setLeadStatus] = useState('new');
  const [leadPriority, setLeadPriority] = useState('medium');
  const todayPlus3 = () => {
    const d = new Date();
    d.setDate(d.getDate() + 3);
    return d.toISOString().split('T')[0];
  };
  const [leadNad, setLeadNad] = useState(todayPlus3());
  const [leadNote, setLeadNote] = useState('First outreach call');
  // Assignee: 'me' (default) or a real user id; we also store a friendly label for the chip
  const [assigneeId, setAssigneeId] = useState('me');
  const [assigneeLabel, setAssigneeLabel] = useState('Myself');
  const [delegatables, setDelegatables] = useState([]);
  const [createLead, setCreateLead] = useState(true);

  // Load delegatable users once (for assignee dropdown)
  useEffect(() => {
    axios.get(API + '/leads/delegatable-users').then(r => {
      // Endpoint returns { users: [...] } — not a flat array
      const list = Array.isArray(r.data) ? r.data : (r.data?.users || []);
      setDelegatables(list);
    }).catch(() => setDelegatables([]));
  }, []);

  // Build async-searchable option list: Myself first, then delegatables
  const fetchAssigneeOptions = async (q) => {
    const qLower = (q || '').toLowerCase();
    const pool = [
      { id: 'me', name: 'Myself', email: '', role: '', _isMe: true },
      ...delegatables.map(u => ({ id: u.id, name: u.name || u.email, email: u.email, role: u.role })),
    ];
    if (!qLower) return pool.slice(0, 20);
    return pool.filter(u => {
      return (u.name || '').toLowerCase().includes(qLower)
        || (u.email || '').toLowerCase().includes(qLower)
        || (u.role || '').toLowerCase().includes(qLower);
    }).slice(0, 20);
  };

  const handleAssigneeSelect = (u) => {
    if (!u) {
      // Clear — show search input (user can re-pick Myself by searching or clearing again)
      setAssigneeId(null);
      setAssigneeLabel(null);
      return;
    }
    setAssigneeId(u.id);
    setAssigneeLabel(u._isMe ? 'Myself' : `${u.name}${u.email ? ` · ${u.email}` : ''}${u.role ? ` (${u.role})` : ''}`);
  };

  const toggleContact = (idx, field, val) => {
    setContacts(prev => prev.map((c, i) => i === idx ? { ...c, [field]: val } : c));
  };

  const selectedContacts = contacts.filter(c => c.selected && (c.email || c.name));

  const handleSubmit = async () => {
    setError('');
    setSaving(true);
    try {
      const payload = {
        domain: defaultDomain,
        name: companyName || defaultCompanyName,
        industry: industry || undefined,
        employee_size: employeeSize || undefined,
        city: city || undefined,
        website: suggestion?.website || `https://${defaultDomain}`,
        contacts: selectedContacts.map(c => ({
          email: c.email || null,
          name: c.name,
          phone: c.phone || null,
          designation: c.designation || '',
          is_primary_contact: !!c.is_primary_contact,
          is_decision_maker: !!c.is_decision_maker,
          influence_level: c.influence_level || 'medium',
        })),
      };
      if (createLead && leadContactEmail) {
        payload.lead = {
          contact_email: leadContactEmail,
          status: leadStatus,
          priority: leadPriority,
          next_action_date: leadNad || null,
          next_action_note: leadNote || null,
          assigned_user_id: (assigneeId && assigneeId !== 'me') ? assigneeId : null,
          source: 'email_suggestion',
        };
      }
      const res = await axios.post(API + '/accounts/suggestions/accept', payload);
      onComplete && onComplete(res.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : (Array.isArray(detail) ? detail.map(d => d.msg).join(', ') : 'Failed to create account'));
    } finally {
      setSaving(false);
    }
  };

  const StepPill = ({ n, label, active, done }) => (
    <div className={'flex items-center gap-2 text-xs ' + (active ? 'text-slate-900 dark:text-white font-semibold' : done ? 'text-green-600 dark:text-green-400' : 'text-slate-400 dark:text-slate-500')}>
      <div className={'w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold ' + (active ? 'bg-blue-600 text-white' : done ? 'bg-green-500 text-white' : 'bg-slate-200 dark:bg-slate-700')}>{done ? '✓' : n}</div>
      <span>{label}</span>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="accept-suggestion-modal">
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between sticky top-0 bg-white dark:bg-slate-800 rounded-t-xl">
          <div>
            <h2 className="text-lg font-bold text-slate-900 dark:text-white">Create Account from Inbox</h2>
            <p className="text-xs text-slate-500 mt-0.5">{defaultDomain}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg" data-testid="accept-modal-close" aria-label="Close">
            <svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        {/* Stepper */}
        <div className="px-6 py-3 border-b border-slate-100 dark:border-slate-700 flex items-center gap-4 bg-slate-50 dark:bg-slate-900/40">
          <StepPill n={1} label="Company" active={step === 1} done={step > 1} />
          <div className="w-6 h-px bg-slate-300 dark:bg-slate-600" />
          <StepPill n={2} label={`Contacts (${selectedContacts.length})`} active={step === 2} done={step > 2} />
          <div className="w-6 h-px bg-slate-300 dark:bg-slate-600" />
          <StepPill n={3} label="Seed Lead" active={step === 3} done={false} />
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {error && <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-600 dark:text-red-400 text-sm" data-testid="accept-modal-error">{error}</div>}

          {step === 1 && (
            <div className="space-y-4" data-testid="accept-step-company">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Company Name *</label>
                <input type="text" value={companyName} onChange={e => setCompanyName(e.target.value)} required className="w-full px-4 py-2 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white" data-testid="accept-company-name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Industry</label>
                  <select value={industry} onChange={e => setIndustry(e.target.value)} className="w-full px-4 py-2 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white" data-testid="accept-company-industry">
                    <option value="">—</option>
                    {['Technology','Finance','Healthcare','Manufacturing','Retail','Education','Real Estate','Telecom','Media','Other'].map(i => <option key={i} value={i}>{i}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Employee Size</label>
                  <select value={employeeSize} onChange={e => setEmployeeSize(e.target.value)} className="w-full px-4 py-2 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white" data-testid="accept-company-size">
                    <option value="">—</option>
                    {['1-10','11-50','51-200','201-500','501-1000','1000+'].map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">City</label>
                <input type="text" value={city} onChange={e => setCity(e.target.value)} className="w-full px-4 py-2 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white" />
              </div>
              <p className="text-xs text-slate-500">You'll add contacts and optionally a seed lead in the next steps.</p>
            </div>
          )}

          {step === 2 && (
            <div data-testid="accept-step-contacts">
              <p className="text-xs text-slate-500 mb-3">Select people from <strong>{defaultDomain}</strong> to add as contacts + stakeholders. All are selected by default.</p>
              {contacts.length === 0 ? (
                <div className="p-8 text-center text-sm text-slate-500">No email senders detected at this domain.</div>
              ) : (
                <div className="space-y-2">
                  {contacts.map((c, idx) => (
                    <div key={c.email || idx} className={'p-3 border rounded-lg transition-colors ' + (c.selected ? 'bg-blue-50/40 dark:bg-blue-900/10 border-blue-200 dark:border-blue-800' : 'bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-700 opacity-60')} data-testid={'accept-contact-row-' + idx}>
                      <div className="flex items-start gap-3">
                        <input type="checkbox" checked={c.selected} onChange={e => toggleContact(idx, 'selected', e.target.checked)} className="mt-1" data-testid={'accept-contact-check-' + idx} />
                        <div className="flex-1 min-w-0 space-y-2">
                          <div className="grid grid-cols-2 gap-2">
                            <input type="text" value={c.name} onChange={e => toggleContact(idx, 'name', e.target.value)} placeholder="Full name" className="px-3 py-1.5 text-sm bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-md text-slate-900 dark:text-white" disabled={!c.selected} />
                            <input type="text" value={c.designation} onChange={e => toggleContact(idx, 'designation', e.target.value)} placeholder="Designation (e.g. VP Sales)" className="px-3 py-1.5 text-sm bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-md text-slate-900 dark:text-white" disabled={!c.selected} />
                          </div>
                          <div className="text-[11px] text-slate-500 truncate">{c.email}</div>
                          <div className="flex items-center gap-4 text-xs">
                            <label className="flex items-center gap-1 cursor-pointer">
                              <input type="checkbox" checked={c.is_primary_contact} onChange={e => toggleContact(idx, 'is_primary_contact', e.target.checked)} disabled={!c.selected} />
                              <span className="text-slate-600 dark:text-slate-400">Primary</span>
                            </label>
                            <label className="flex items-center gap-1 cursor-pointer">
                              <input type="checkbox" checked={c.is_decision_maker} onChange={e => toggleContact(idx, 'is_decision_maker', e.target.checked)} disabled={!c.selected} />
                              <span className="text-slate-600 dark:text-slate-400">Decision Maker</span>
                            </label>
                            <select value={c.influence_level} onChange={e => toggleContact(idx, 'influence_level', e.target.value)} disabled={!c.selected} className="px-2 py-0.5 text-xs bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded text-slate-700 dark:text-slate-300">
                              <option value="low">Low influence</option>
                              <option value="medium">Medium influence</option>
                              <option value="high">High influence</option>
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4" data-testid="accept-step-lead">
              <div className="flex items-center gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                <input type="checkbox" checked={createLead} onChange={e => setCreateLead(e.target.checked)} data-testid="accept-create-lead-toggle" />
                <span className="text-sm font-medium text-amber-800 dark:text-amber-300">Create a seed Lead from one of the contacts</span>
              </div>
              {createLead && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Convert which contact?</label>
                    <select value={leadContactEmail} onChange={e => setLeadContactEmail(e.target.value)} className="w-full px-4 py-2 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white" data-testid="accept-lead-contact-select">
                      {selectedContacts.map(c => <option key={c.email} value={c.email}>{`${c.name} <${c.email}>${c.designation ? ' • ' + c.designation : ''}`}</option>)}
                    </select>
                    {selectedContacts.length === 0 && <p className="text-xs text-red-500 mt-1">Go back to Step 2 and select at least one contact.</p>}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Lead Status</label>
                      <select value={leadStatus} onChange={e => setLeadStatus(e.target.value)} className="w-full px-4 py-2 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white" data-testid="accept-lead-status">
                        {['new','contacted','qualified','proposal','negotiation'].map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Priority</label>
                      <select value={leadPriority} onChange={e => setLeadPriority(e.target.value)} className="w-full px-4 py-2 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white" data-testid="accept-lead-priority">
                        {['low','medium','high','critical'].map(p => <option key={p} value={p}>{p}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Next Action Date (NAD) *</label>
                      <input type="date" value={leadNad} onChange={e => setLeadNad(e.target.value)} min={new Date().toISOString().split('T')[0]} className="w-full px-4 py-2 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white" data-testid="accept-lead-nad" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">NAD Note</label>
                      <input type="text" value={leadNote} onChange={e => setLeadNote(e.target.value)} placeholder="e.g. Intro call" className="w-full px-4 py-2 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white" data-testid="accept-lead-note" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Assign to</label>
                    <AsyncSearchSelect
                      value={assigneeId}
                      valueLabel={assigneeLabel}
                      onSelect={handleAssigneeSelect}
                      fetchOptions={fetchAssigneeOptions}
                      placeholder="Search by name, email, or role…"
                      testId="accept-lead-assignee"
                      minChars={0}
                      debounceMs={150}
                      renderOption={(u) => (
                        <div className="flex flex-col">
                          <span className="font-medium text-slate-900 dark:text-white truncate">{u.name}</span>
                          {!u._isMe && (
                            <span className="text-xs text-slate-500 dark:text-slate-400 truncate">
                              {u.email}{u.role ? ` · ${u.role}` : ''}
                            </span>
                          )}
                          {u._isMe && (
                            <span className="text-xs text-blue-600 dark:text-blue-400">Keep this lead for yourself</span>
                          )}
                        </div>
                      )}
                    />
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between bg-slate-50 dark:bg-slate-900/40 rounded-b-xl">
          <div className="text-xs text-slate-500">
            {step === 1 ? 'Step 1 of 3' : step === 2 ? `Step 2 of 3 • ${selectedContacts.length} selected` : 'Step 3 of 3'}
          </div>
          <div className="flex items-center gap-2">
            {step > 1 && (
              <button onClick={() => setStep(step - 1)} disabled={saving} className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg disabled:opacity-50" data-testid="accept-modal-back">Back</button>
            )}
            {step < 3 ? (
              <button onClick={() => setStep(step + 1)} disabled={step === 1 && !companyName.trim()} className="px-4 py-2 text-sm font-semibold bg-slate-900 text-white dark:bg-white dark:text-slate-900 rounded-lg hover:bg-slate-800 dark:hover:bg-slate-100 disabled:opacity-50" data-testid="accept-modal-next">Next</button>
            ) : (
              <button onClick={handleSubmit} disabled={saving || !companyName.trim() || (createLead && !leadContactEmail)} className="px-4 py-2 text-sm font-semibold bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2" data-testid="accept-modal-confirm">
                {saving && <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path></svg>}
                {saving ? 'Creating…' : 'Create Account'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────── Main view

export default function AccountsView() {
  const [searchParams, setSearchParams] = useSearchParams();
  const VALID_MODES = ['accounts', 'directory', 'suggestions'];
  const initialMode = VALID_MODES.includes(searchParams.get('mode')) ? searchParams.get('mode') : 'accounts';
  const [mode, setMode] = useState(initialMode); // 'accounts' | 'directory' | 'suggestions'
  const [suggestionsCount, setSuggestionsCount] = useState(0);

  // Keep URL query param in sync with mode (so /accounts?mode=directory deep-links work)
  const handleModeChange = (newMode) => {
    setMode(newMode);
    const next = new URLSearchParams(searchParams);
    if (newMode === 'accounts') next.delete('mode');
    else next.set('mode', newMode);
    setSearchParams(next, { replace: true });
  };

  // React to external URL changes (e.g., redirect from /companies → /accounts?mode=directory)
  useEffect(() => {
    const urlMode = searchParams.get('mode');
    const effective = VALID_MODES.includes(urlMode) ? urlMode : 'accounts';
    if (effective !== mode) setMode(effective);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Accounts state
  const [accounts, setAccounts] = useState([]);
  const [accPagination, setAccPagination] = useState({ page: 1, limit: 25, total: 0, pages: 0 });
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('last_activity');
  const [selectedId, setSelectedId] = useState(null);
  const [selectedName, setSelectedName] = useState('');
  const [timeline, setTimeline] = useState([]);
  const [tlPagination, setTlPagination] = useState(null);
  const [tlFilter, setTlFilter] = useState('all');
  const [intel, setIntel] = useState(null);
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [loadingTimeline, setLoadingTimeline] = useState(false);
  const [loadingIntel, setLoadingIntel] = useState(false);

  // Suggestions state
  const [suggestions, setSuggestions] = useState([]);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  const [suggestionDetail, setSuggestionDetail] = useState(null);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [loadingSuggestionDetail, setLoadingSuggestionDetail] = useState(false);
  const [acceptingSuggestion, setAcceptingSuggestion] = useState(null);   // opens the wizard modal
  const [acceptingDetail, setAcceptingDetail] = useState(null);           // preloaded detail for the modal
  const [quickCreatingDomain, setQuickCreatingDomain] = useState(null);   // domain currently being one-click-created

  const searchTimer = useRef(null);

  const fetchAccounts = useCallback((page, q, sort) => {
    setLoadingAccounts(true);
    let params = '?page=' + page + '&limit=25';
    if (q) params += '&search=' + encodeURIComponent(q);
    if (sort) params += '&sort_by=' + sort;
    return axios.get(API + '/accounts/overview' + params).then(r => {
      setAccounts(r.data.data || []);
      setAccPagination(r.data.pagination || {});
    }).catch(() => toast.error('Failed to load accounts')).finally(() => setLoadingAccounts(false));
  }, []);

  const fetchSuggestionsCount = useCallback(() => {
    axios.get(API + '/accounts/suggestions/count').then(r => setSuggestionsCount(r.data?.count || 0)).catch(() => {});
  }, []);

  const fetchSuggestions = useCallback(() => {
    setLoadingSuggestions(true);
    axios.get(API + '/accounts/suggestions?limit=100').then(r => {
      setSuggestions(r.data?.data || []);
    }).catch(() => toast.error('Failed to load suggestions')).finally(() => setLoadingSuggestions(false));
  }, []);

  useEffect(() => { fetchAccounts(1, '', sortBy); fetchSuggestionsCount(); }, [fetchAccounts, fetchSuggestionsCount, sortBy]);

  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => fetchAccounts(1, search, sortBy), 400);
    return () => clearTimeout(searchTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  useEffect(() => {
    if (mode === 'suggestions' && suggestions.length === 0) fetchSuggestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  function handleSelectAccount(id, nameOverride) {
    const acc = accounts.find(a => a.id === id);
    setSelectedId(id);
    setSelectedName(nameOverride || (acc ? acc.name : ''));
    setTlFilter('all');
    setLoadingTimeline(true);
    setTimeline([]);
    axios.get(API + '/accounts/' + id + '/timeline?limit=30').then(r => {
      setTimeline(r.data.data || []);
      setTlPagination(r.data.pagination || null);
    }).catch(() => toast.error('Failed to load timeline')).finally(() => setLoadingTimeline(false));
    setLoadingIntel(true);
    setIntel(null);
    axios.get(API + '/accounts/' + id + '/intelligence').then(r => {
      setIntel(r.data);
      // If we didn't have a name (new account), pull it from intelligence response
      if (!nameOverride && !acc && r.data?.company?.name) setSelectedName(r.data.company.name);
    })
      .catch(() => toast.error('Failed to load intelligence')).finally(() => setLoadingIntel(false));
  }

  // Refresh intel for the currently-selected account (used after accepting suggestions)
  function refreshIntel() {
    if (!selectedId) return;
    axios.get(API + '/accounts/' + selectedId + '/intelligence').then(r => {
      setIntel(r.data);
    }).catch(() => {});
  }

  function handleLoadMoreTimeline() {
    if (!tlPagination || !selectedId) return;
    const nextPage = tlPagination.page + 1;
    axios.get(API + '/accounts/' + selectedId + '/timeline?page=' + nextPage + '&limit=30').then(r => {
      setTimeline(prev => prev.concat(r.data.data || []));
      setTlPagination(r.data.pagination || null);
    }).catch(() => toast.error('Failed to load more events'));
  }

  function handleQuickAction(kind, leadId) {
    if (kind === 'send_email') {
      window.location.href = '/email';
      return;
    }
    if (!leadId) { toast.error('This account has no lead yet — create one first'); return; }
    // For all lead-centric actions, navigate into the lead detail page
    window.location.href = '/leads/' + leadId + '?qa=' + kind;
  }

  function handleSelectSuggestion(domain) {
    const s = suggestions.find(x => x.domain === domain);
    setSelectedDomain(domain);
    setSelectedSuggestion(s || null);
    setSuggestionDetail(null);
    setLoadingSuggestionDetail(true);
    axios.get(API + '/accounts/suggestions/' + domain).then(r => setSuggestionDetail(r.data))
      .catch(() => toast.error('Failed to load suggestion')).finally(() => setLoadingSuggestionDetail(false));
  }

  function handleAcceptSuggestion(s) {
    // Load detail first, then open the wizard modal (so contacts list is seeded)
    setAcceptingSuggestion(s); // shows modal in loading state via `detail === null`
    if (suggestionDetail && selectedDomain === s.domain) {
      setAcceptingDetail(suggestionDetail);
      return;
    }
    setAcceptingDetail(null);
    axios.get(API + '/accounts/suggestions/' + s.domain).then(r => {
      setAcceptingDetail(r.data || { contacts_at_domain: [] });
    }).catch(() => {
      setAcceptingDetail({ contacts_at_domain: [] });
    });
  }

  // ─── One-click create — bypasses the 3-step wizard ────────────────────
  // Auto-fills name + website + ALL contacts from the loaded detail and
  // POSTs straight to /accounts/suggestions/accept. Used by the prominent
  // "Create account" button in the suggestion detail panel.
  async function handleQuickCreate(s, detail) {
    if (!s) return;
    setQuickCreatingDomain(s.domain);
    try {
      const d = detail || suggestionDetail || { contacts_at_domain: [] };
      const payload = {
        domain: s.domain,
        name: (s.name_guess || '').trim() || s.domain,
        website: s.website || `https://${s.domain}`,
        contacts: (d.contacts_at_domain || []).slice(0, 25).map(c => ({
          email: c.email || null,
          name: c.name || (c.email || '').split('@')[0],
          phone: null,
          designation: '',
          is_primary_contact: false,
          is_decision_maker: false,
          influence_level: 'medium',
        })),
      };
      const res = await axios.post(API + '/accounts/suggestions/accept', payload);
      handleAcceptComplete(res.data);
    } catch (err) {
      const detailMsg = err.response?.data?.detail;
      const msg = typeof detailMsg === 'string' ? detailMsg : (Array.isArray(detailMsg) ? detailMsg.map(x => x.msg).join(', ') : 'Failed to create account');
      toast.error(msg);
    } finally {
      setQuickCreatingDomain(null);
    }
  }

  function handleAcceptComplete(result) {
    const s = acceptingSuggestion;
    const summary = [
      `Account "${result?.company?.name || s?.name_guess}" created`,
      result?.contacts_created ? `${result.contacts_created} contact(s) added` : null,
      result?.stakeholders_added ? `${result.stakeholders_added} stakeholder(s) linked` : null,
      result?.lead_id ? `Lead created` : null,
    ].filter(Boolean).join(' • ');
    toast.success(summary);
    // Remove from suggestions list
    if (s) {
      setSuggestions(prev => prev.filter(x => x.domain !== s.domain));
      setSuggestionsCount(c => Math.max(0, c - 1));
      if (selectedDomain === s.domain) { setSelectedDomain(null); setSelectedSuggestion(null); setSuggestionDetail(null); }
    }
    setAcceptingSuggestion(null);
    setAcceptingDetail(null);
    // Jump to Engagement tab with the new company pre-selected (pass name override
    // since the freshly created company isn't in the accounts list yet)
    handleModeChange('accounts');
    const newCompanyName = result?.company?.name || s?.name_guess || '';
    fetchAccounts(1, search, sortBy).then(() => {
      if (result?.company_id) {
        setTimeout(() => handleSelectAccount(result.company_id, newCompanyName), 200);
      }
    });
  }

  function handleDismissSuggestion(s) {
    axios.post(API + '/accounts/suggestions/dismiss', { domain: s.domain }).then(() => {
      toast.success('Dismissed ' + s.domain);
      setSuggestions(prev => prev.filter(x => x.domain !== s.domain));
      setSuggestionsCount(c => Math.max(0, c - 1));
      if (selectedDomain === s.domain) { setSelectedDomain(null); setSelectedSuggestion(null); setSuggestionDetail(null); }
    }).catch(err => toast.error(err.response?.data?.detail || 'Failed to dismiss'));
  }

  const leadsForActions = intel?.leads || [];

  return (
    <div className="h-[calc(100vh-80px)]" data-testid="accounts-view">
      {/* Top-level tab switcher */}
      <div className="flex items-center gap-1 mb-3 px-1" data-testid="accounts-mode-tabs">
        <button
          onClick={() => handleModeChange('accounts')}
          className={'px-4 py-2 text-sm font-semibold rounded-lg transition-colors ' + (mode === 'accounts' ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900' : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700')}
          data-testid="tab-accounts"
        >Engagement</button>
        <button
          onClick={() => handleModeChange('directory')}
          className={'px-4 py-2 text-sm font-semibold rounded-lg transition-colors ' + (mode === 'directory' ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900' : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700')}
          data-testid="tab-directory"
        >Directory</button>
        <button
          onClick={() => handleModeChange('suggestions')}
          className={'px-4 py-2 text-sm font-semibold rounded-lg transition-colors flex items-center gap-2 ' + (mode === 'suggestions' ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900' : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700')}
          data-testid="tab-suggestions"
        >
          Suggestions
          {suggestionsCount > 0 && <span className="px-1.5 py-0.5 bg-blue-500 text-white text-[10px] font-bold rounded-full">{suggestionsCount}</span>}
        </button>
      </div>
      {mode === 'directory' ? (
        <div className="h-[calc(100%-3rem)] overflow-auto border border-slate-200 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-800" data-testid="accounts-directory-pane">
          <CompaniesPage embedded />
        </div>
      ) : (
      <div className="flex h-[calc(100%-3rem)] border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden bg-white dark:bg-slate-800">
        {mode === 'accounts' ? (
          <>
            <div className="w-72 flex-shrink-0 border-r border-slate-200 dark:border-slate-700">
              <AccountListPanel
                accounts={accounts} selectedId={selectedId} onSelect={handleSelectAccount}
                search={search} setSearch={setSearch}
                sortBy={sortBy} setSortBy={setSortBy}
                loading={loadingAccounts}
                pagination={accPagination} onPageChange={(p) => fetchAccounts(p, search, sortBy)}
              />
            </div>
            <div className="flex-1 border-r border-slate-200 dark:border-slate-700">
              <TimelinePanel
                timeline={timeline} loading={loadingTimeline} companyName={selectedName}
                pagination={tlPagination} onLoadMore={handleLoadMoreTimeline}
                filter={tlFilter} setFilter={setTlFilter}
                leads={leadsForActions}
                onQuickAction={handleQuickAction}
              />
            </div>
            <div className="w-80 flex-shrink-0">
              <IntelligencePanel intel={intel} loading={loadingIntel} onContactsChanged={refreshIntel} />
            </div>
          </>
        ) : (
          <>
            <div className="w-80 flex-shrink-0 border-r border-slate-200 dark:border-slate-700">
              <SuggestionListPanel
                suggestions={suggestions} selectedDomain={selectedDomain}
                onSelect={handleSelectSuggestion}
                onAccept={handleAcceptSuggestion}
                onDismiss={handleDismissSuggestion}
                loading={loadingSuggestions}
                onRefresh={fetchSuggestions}
              />
            </div>
            <div className="flex-1">
              <SuggestionDetailPanel
                suggestion={selectedSuggestion}
                detail={suggestionDetail}
                loading={loadingSuggestionDetail}
                onAccept={handleAcceptSuggestion}
                onQuickCreate={handleQuickCreate}
                onDismiss={handleDismissSuggestion}
                quickCreating={quickCreatingDomain && quickCreatingDomain === selectedSuggestion?.domain}
              />
            </div>
          </>
        )}
      </div>
      )}
      {/* Accept Suggestion wizard modal (renders only once detail is loaded) */}
      {acceptingSuggestion && acceptingDetail && (
        <AcceptSuggestionModal
          suggestion={acceptingSuggestion}
          detail={acceptingDetail}
          onClose={() => { setAcceptingSuggestion(null); setAcceptingDetail(null); }}
          onComplete={handleAcceptComplete}
        />
      )}
      {/* Tiny loader while detail is fetching */}
      {acceptingSuggestion && !acceptingDetail && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center" data-testid="accept-modal-loading">
          <div className="bg-white dark:bg-slate-800 px-6 py-4 rounded-lg shadow-xl flex items-center gap-3">
            <div className="w-5 h-5 border-2 border-slate-200 border-t-slate-600 rounded-full animate-spin"></div>
            <span className="text-sm text-slate-700 dark:text-slate-300">Loading contacts at {acceptingSuggestion.domain}…</span>
          </div>
        </div>
      )}
    </div>
  );
}
